/**
 * Adventure Lobby Page
 *
 * Waiting room after creating an adventure. Displays invite code for the partner
 * to join, shows player slots, and auto-navigates to the map when the partner
 * connects and the adventure becomes active.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdventureStore, type AdventureData, type WorldData } from '../stores/useAdventureStore';
import { adventureSocket } from '../services/adventure-socket';
import { useAuth } from '../contexts/AuthContext';
import { PageAtmosphere } from '../components/atmosphere';
import './AdventureLobbyPage.css';

export const AdventureLobbyPage: React.FC = () => {
  const { adventureId } = useParams<{ adventureId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const navigatingToMapRef = useRef(false);

  const {
    adventure,
    world,
    partnerConnected,
    setFullState,
    setPartnerConnected,
  } = useAdventureStore();

  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Connecting to adventure...');

  // Connect socket on mount
  useEffect(() => {
    if (authLoading || !user || !adventureId) return;

    let mounted = true;

    const connectToAdventure = async () => {
      try {
        setConnectionStatus('connecting');
        setStatusMessage('Connecting to adventure...');

        const state = await adventureSocket.connect(adventureId);

        if (!mounted) return;

        if (state) {
          setFullState(
            state.adventure as AdventureData,
            state.world as WorldData,
            state.partnerConnected
          );
          setConnectionStatus('connected');
          setStatusMessage('Waiting for partner to join...');
        }
      } catch (err) {
        if (!mounted) return;
        setConnectionStatus('error');
        setConnectionError(err instanceof Error ? err.message : 'Failed to connect');
      }
    };

    // Small delay for auth token to be available
    const timer = setTimeout(connectToAdventure, 150);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [authLoading, user, adventureId, setFullState]);

  // Register socket event listeners
  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    adventureSocket.onPartnerConnected((data) => {
      setPartnerConnected(true);
      setStatusMessage(`${data.displayName} has joined! Starting adventure...`);
    });

    adventureSocket.onError((data) => {
      setConnectionError(data.message);
    });

    return () => {
      adventureSocket.clearAdventureListeners();
    };
  }, [connectionStatus, setPartnerConnected]);

  // Auto-navigate to map when partner joins (adventure becomes active)
  useEffect(() => {
    if (partnerConnected && adventureId && !navigatingToMapRef.current) {
      navigatingToMapRef.current = true;
      const timer = setTimeout(() => {
        navigate(`/adventure/map/${adventureId}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [partnerConnected, adventureId, navigate]);

  // Cleanup on unmount (only disconnect if NOT navigating to map)
  useEffect(() => {
    return () => {
      if (!navigatingToMapRef.current) {
        adventureSocket.clearAdventureListeners();
        adventureSocket.disconnect();
      }
    };
  }, []);

  const handleCopyCode = useCallback(async () => {
    const code = adventure?.inviteCode;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // Fallback: select text for manual copy
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  }, [adventure?.inviteCode]);

  const handleCancel = useCallback(() => {
    adventureSocket.clearAdventureListeners();
    adventureSocket.disconnect();
    navigate('/adventure');
  }, [navigate]);

  // Loading / auth states
  if (authLoading) {
    return (
      <PageAtmosphere page="adventure-lobby">
        <div className="adventure-lobby">
          <div className="lobby-loading">
            <div className="loading-spinner" />
            <p>Authenticating...</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  if (connectionStatus === 'error') {
    return (
      <PageAtmosphere page="adventure-lobby">
        <div className="adventure-lobby">
          <div className="lobby-error">
            <h2>Connection Error</h2>
            <p>{connectionError || 'Failed to connect to adventure'}</p>
            <button onClick={() => navigate('/adventure')}>Back to Hub</button>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  if (connectionStatus === 'connecting' || !adventure) {
    return (
      <PageAtmosphere page="adventure-lobby">
        <div className="adventure-lobby">
          <div className="lobby-loading">
            <div className="loading-spinner" />
            <p>Connecting to adventure...</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  const themeEmoji: Record<string, string> = {
    fire: '\uD83C\uDF0B', ice: '\u2744\uFE0F', forest: '\uD83C\uDF32',
    dark: '\uD83C\uDF11', desert: '\uD83C\uDFDC\uFE0F', ocean: '\uD83C\uDF0A',
    mountain: '\u26F0\uFE0F',
  };

  return (
    <PageAtmosphere page="adventure-lobby">
    <div className="adventure-lobby">
      <div className="lobby-content">
        {/* Header */}
        <div className="lobby-header">
          <h1>
            {world ? `${themeEmoji[world.theme] || '\u2694\uFE0F'} ${world.name}` : 'Adventure Lobby'}
          </h1>
          {world?.narrative && (
            <p className="lobby-narrative">"{world.narrative}"</p>
          )}
        </div>

        {/* Invite Code Section */}
        <div className="invite-section">
          <h3>Share this invite code with your partner</h3>
          <div className="invite-code-row">
            <span className="invite-code">{adventure.inviteCode || '------'}</span>
            <button className="copy-button" onClick={handleCopyCode}>
              {codeCopied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Player Slots */}
        <div className="player-slots">
          <div className="player-slot player-slot-filled">
            <div className="player-slot-icon">{'\uD83D\uDC51'}</div>
            <div className="player-slot-info">
              <span className="player-slot-label">Host</span>
              <span className="player-slot-name">
                {adventure.players.host.displayName}
              </span>
            </div>
            <span className="player-slot-status player-status-ready">Ready</span>
          </div>

          <div className={`player-slot ${partnerConnected ? 'player-slot-filled' : 'player-slot-waiting'}`}>
            <div className="player-slot-icon">
              {partnerConnected ? '\uD83E\uDD1D' : '\u231B'}
            </div>
            <div className="player-slot-info">
              <span className="player-slot-label">Partner</span>
              <span className="player-slot-name">
                {partnerConnected && adventure.players.partner
                  ? adventure.players.partner.displayName
                  : 'Waiting for partner...'}
              </span>
            </div>
            <span className={`player-slot-status ${partnerConnected ? 'player-status-ready' : 'player-status-waiting'}`}>
              {partnerConnected ? 'Joined!' : 'Waiting'}
            </span>
          </div>
        </div>

        {/* Status Message */}
        <div className="lobby-status">
          <p>{statusMessage}</p>
          {partnerConnected && (
            <div className="lobby-countdown">
              Navigating to map...
            </div>
          )}
        </div>

        {/* Cancel Button */}
        {!partnerConnected && (
          <button className="cancel-button" onClick={handleCancel}>
            Cancel &amp; Return to Hub
          </button>
        )}
      </div>
    </div>
    </PageAtmosphere>
  );
};
